import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTrackEvent } from 'common';
import { API } from '@xbto/api-client';
import { theme } from '@xbto/design-system';
import { Icon, TextBody, Row } from '../../atoms';
import { Hypertext } from '../hypertext';
const levelTextColors = {
    [API.MessageTemplateAlertLevel.Brand]: 'primary',
    [API.MessageTemplateAlertLevel.Error]: 'error',
    [API.MessageTemplateAlertLevel.Success]: 'success',
    [API.MessageTemplateAlertLevel.Warning]: 'warning',
    [API.MessageTemplateAlertLevel.Info]: 'info',
};
const levelBackgroundColors = {
    [API.MessageTemplateAlertLevel.Brand]: 'brand-light',
    [API.MessageTemplateAlertLevel.Error]: 'error',
    [API.MessageTemplateAlertLevel.Success]: 'success',
    [API.MessageTemplateAlertLevel.Warning]: 'warning',
    [API.MessageTemplateAlertLevel.Info]: 'info',
};
const truncateWithEllipsis = (text, limit) => text.length > limit ? (text === null || text === void 0 ? void 0 : text.slice(0, limit).trim()) + '...' : text;
export const Banner = memo(({ id, level = API.MessageTemplateAlertLevel.Info, message, onDismiss, title = '', url, urlText = 'Learn more', }) => {
    // Vars
    const backgroundColorName = levelBackgroundColors[level];
    const textColorName = levelTextColors[level];
    const dismissable = !!onDismiss;
    // Hooks
    const trackEvent = useTrackEvent();
    const { truncTitle, truncMessage, truncUrlText } = useMemo(() => ({
        truncTitle: truncateWithEllipsis(title, 30),
        truncMessage: truncateWithEllipsis(message, 120),
        truncUrlText: truncateWithEllipsis(urlText, 20),
    }), [message, title, urlText]);
    // Handlers
    const handleDismiss = useCallback(() => {
        trackEvent('banner_dismiss_clicked', { banner: title });
        onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss(id);
    }, [id, onDismiss, title, trackEvent]);
    const handleTrackCtaEvent = useCallback(() => {
        trackEvent('banner_cta_clicked', { banner: title });
    }, [title, trackEvent]);
    // Render
    return (_jsxs(View, { style: [
            $.container,
            { backgroundColor: theme.color.background[backgroundColorName] },
        ], children: [_jsxs(Row.L, { style: $.content, children: [_jsxs(Row.L, { style: $.contentText, children: [!!title && (_jsx(TextBody, { em: true, color: textColorName, style: $.item, children: truncTitle })), _jsx(TextBody, { color: textColorName, style: $.item, children: truncMessage })] }), !!url && (_jsx(View, { style: [$.item, $.link], children: _jsx(Hypertext, { color: textColorName, href: url, hrefAttrs: { target: '_blank' }, containerStyle: { alignItems: 'flex-start' }, onPress: handleTrackCtaEvent, children: truncUrlText }) }))] }), _jsx(Row.R, { style: $.close, children: dismissable && (_jsx(Pressable, { onPress: handleDismiss, children: _jsx(Icon, { name: "Close", size: "sm", color: textColorName }) })) })] }));
});
const $ = StyleSheet.create({
    container: {
        width: '100%',
        paddingVertical: theme.spacing['4'],
        paddingHorizontal: theme.spacing['4'],
        borderRadius: Number(theme.radius.sm),
        marginVertical: theme.spacing['4'],
        marginHorizontal: 0,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    content: {
        flex: 1,
        justifyContent: 'space-between',
        columnGap: theme.spacing['2'],
        rowGap: theme.spacing['2'],
        flexWrap: 'wrap',
    },
    contentText: {
        maxWidth: '100%',
        columnGap: theme.spacing['2'],
        flexWrap: 'wrap',
        rowGap: theme.spacing['2'],
    },
    item: { flexGrow: 0, flexShrink: 1, flexBasis: 'auto' },
    link: { flexShrink: 0 },
    close: {
        paddingLeft: theme.spacing['4'],
        paddingVertical: theme.spacing['0.5'],
        alignSelf: 'flex-start',
        // simulate the underline to vertically align properly with link
        borderTopWidth: 1,
        borderTopColor: 'transparent',
    },
});
